import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { translations } from '@/translations';
import { supabase } from '@/integrations/supabase/client';

type Language = 'mn' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, langOverride?: string | boolean) => string;
  isEnglish: boolean;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>('mn');
  const [user, setUser] = useState<any>(null);
  const isEnglish = language === 'en';

  // Load user and language preference on mount
  useEffect(() => {
    loadUserAndLanguage();
  }, []);

  const loadUserAndLanguage = async () => {
    // Get current user
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    setUser(currentUser);

    if (currentUser) {
      // Load from database for logged-in users
      const { data } = await supabase
        .from('user_ai_preferences')
        .select('preferred_lang')
        .eq('user_id', currentUser.id)
        .single();
      
      if (data?.preferred_lang) {
        setLanguageState(data.preferred_lang as Language);
      } else {
        // Load from localStorage as fallback
        const saved = localStorage.getItem('language') as Language;
        if (saved) setLanguageState(saved);
      }
    } else {
      // Load from localStorage for anonymous users
      const saved = localStorage.getItem('language') as Language;
      if (saved) {
        setLanguageState(saved);
      } else {
        localStorage.setItem('language', 'mn');
      }
    }
  };

  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    
    if (user) {
      // Save to database for logged-in users
      await supabase
        .from('user_ai_preferences')
        .upsert({
          user_id: user.id,
          preferred_lang: lang,
          updated_at: new Date().toISOString()
        });
    }
  };

  const toggleLanguage = () => {
    const newLang = language === 'mn' ? 'en' : 'mn';
    setLanguage(newLang);
  };

  const t = (key: string, langOverride?: string | boolean): string => {
    let lang = language;
    let isLangCodeOverride = false;
    
    // Handle both boolean (legacy) and string (lang code) overrides
    if (typeof langOverride === 'boolean') {
      lang = langOverride ? 'en' : 'mn';
      isLangCodeOverride = true;
    } else if (typeof langOverride === 'string' && (langOverride === 'en' || langOverride === 'mn')) {
      lang = langOverride as Language;
      isLangCodeOverride = true;
    }
    
    // Try to find key in translations
    const keys = key.split('.');
    let value: any = translations[lang];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    // If found in translations and it's a string, return it
    if (value && typeof value === 'string') {
      return value;
    }
    
    // If not found and langOverride is a string (but not a lang code), treat as inline bilingual text
    // First param = Mongolian text, second param = English text
    if (typeof langOverride === 'string' && !isLangCodeOverride) {
      return lang === 'en' ? langOverride : key;
    }
    
    // Otherwise return the key itself (fallback)
    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isEnglish, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}